// The stage: one WebGL renderer, the room, the camera rig and the frame loop.
// Actors (devices, cases, the laptop screen) register themselves here.

import * as THREE from "three";
import { loadRoom, type RoomInstance } from "./room.ts";
import { CameraRig, poseFrom, type Pose } from "./rig.ts";
import { Tweens } from "./tween.ts";
import type { CameraName } from "../room/contract.ts";
import { LiveShadows } from "./live-shadows.ts";
import { Clearance } from "./clearance.ts";

export interface StageOptions {
  /** Directory that holds the Draco decoder. */
  dracoPath: string;
}

export type FrameListener = (dt: number, now: number) => void;

/** How often a resting room draws, in milliseconds. */
const RESTING_MS = 250;

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly tweens = new Tweens();
  readonly rig = new CameraRig(this.tweens);
  /** Live objects lit by the room's environment sit in this group. */
  readonly actors = new THREE.Group();
  room: RoomInstance | null = null;
  private listeners = new Set<FrameListener>();
  /** What live objects cast on the baked room. */
  readonly shadows = new LiveShadows();
  /** Where the room is solid, for moving live objects through it. */
  clearance: Clearance | null = null;
  private raf = 0;
  private last = 0;
  private resting = false;
  private drawn = 0;
  private elapsed = 0;
  private still = matchMedia("(prefers-reduced-motion: reduce)");

  constructor(readonly canvas: HTMLCanvasElement, private options: StageOptions) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "high-performance" });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // The atlases carry the bake's display transform; live objects get the
    // same Khronos neutral curve and exposure so both read as one picture.
    this.renderer.toneMapping = THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = 0.9;
    // The key light's shadows on live objects: the room and the objects
    // themselves cast them. They are redrawn with the live shadow maps.
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.autoUpdate = false;
    this.actors.name = "actors";
    this.scene.add(this.actors);
    const observer = new ResizeObserver(() => this.resize());
    observer.observe(canvas);
    this.resize();
  }

  get camera(): THREE.PerspectiveCamera {
    return this.rig.camera;
  }

  private resize(): void {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(width, height, false);
    this.rig.resize(width / height);
  }

  async setRoom(baseUrl: string): Promise<RoomInstance> {
    const next = await loadRoom(baseUrl, this.renderer, this.options.dracoPath, this.shadows);
    if (this.room) {
      this.scene.remove(this.room.group);
      this.room.dispose();
    }
    this.room = next;
    this.scene.add(next.group);
    const { min, max } = next.manifest.bounds;
    const bounds = new THREE.Box3(
      new THREE.Vector3(Math.min(min[0], max[0]), Math.min(min[1], max[1]), Math.min(min[2], max[2])),
      new THREE.Vector3(Math.max(min[0], max[0]), Math.max(min[1], max[1]), Math.max(min[2], max[2])),
    );
    // Where a live object can be: on any anchor, or in the air between two.
    // The margins hold the largest device, the lift of a flight and a case
    // pulled off its ledge.
    const { deviceSlots, appSlots, playSpot, buildSpot } = next.manifest.anchors;
    const reach = new THREE.Box3().setFromPoints(
      [...deviceSlots, ...appSlots, playSpot, buildSpot].map((anchor) => new THREE.Vector3().fromArray(anchor.position)));
    reach.min.add(new THREE.Vector3(-0.5, -0.1, -0.5));
    reach.max.add(new THREE.Vector3(0.5, 0.65, 0.5));
    this.shadows.fit(bounds, reach, new THREE.Vector3().fromArray(next.manifest.key.direction), next.manifest.key.angle);
    this.clearance = new Clearance(next.group, reach.clone().expandByScalar(0.25));
    this.rig.setLimits(next.manifest.orbit, bounds);
    this.rig.set(poseFrom(next.manifest.cameras.overview), true);
    this.light(next);
    return next;
  }

  /** Redraw the live shadows on the next frame: something that casts has moved. */
  refreshShadows(): void {
    this.shadows.invalidate();
  }

  /** Light the live objects from the baked room: its image as environment, its lamps as lights. */
  private light(room: RoomInstance): void {
    for (const child of [...this.scene.children]) {
      if ((child as THREE.Light).isLight) this.scene.remove(child);
    }
    const play = new THREE.Vector3().fromArray(room.manifest.anchors.playSpot.position);
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.actors.visible = false;
    for (const glow of room.glows) glow.visible = false;
    const previous = this.scene.environment;
    this.scene.environment = pmrem.fromScene(this.scene, 0.02, 0.05, 40, {
      position: play.clone().add(new THREE.Vector3(0, 0.35, -0.2)),
    }).texture;
    previous?.dispose();
    pmrem.dispose();
    this.actors.visible = true;
    for (const glow of room.glows) glow.visible = true;
    this.scene.environmentIntensity = 2.4;

    this.scene.add(new THREE.HemisphereLight(0xd8c9ff, 0x7a5646, 0.4));
    // The same key the bake used, from the same direction and through the
    // same box as the live shadow map, so a device's lit side, the shadow it
    // takes from the room and the shadow it casts on the room agree.
    const key = new THREE.DirectionalLight(0xffe2c2, 5.6);
    const view = this.shadows.keyCamera;
    key.position.copy(view.position);
    view.getWorldDirection(key.target.position).add(view.position);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    Object.assign(key.shadow.camera, { left: view.left, right: view.right, top: view.top, bottom: view.bottom, near: view.near, far: view.far });
    key.shadow.camera.updateProjectionMatrix();
    key.shadow.bias = -0.0004;
    key.shadow.normalBias = 0.004;
    this.scene.add(key, key.target);
    // Every spot the bake used also lights the live objects under it.
    for (const source of room.manifest.lights) {
      if (source.type !== "spot" || !source.direction) continue;
      const lamp = new THREE.SpotLight(new THREE.Color().fromArray(source.color), Math.sqrt(source.watts) * 1.6, 2.5, (source.angle ?? 1) / 2, 0.7, 1.4);
      lamp.position.fromArray(source.position);
      lamp.target.position.fromArray(source.position).add(new THREE.Vector3().fromArray(source.direction));
      this.scene.add(lamp, lamp.target);
    }
  }

  cameraPose(name: CameraName): Pose {
    if (!this.room) throw new Error("no room loaded");
    return poseFrom(this.room.manifest.cameras[name]);
  }

  onFrame(listener: FrameListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  start(): void {
    if (this.raf) return;
    this.last = performance.now();
    const loop = (now: number) => {
      this.raf = requestAnimationFrame(loop);
      const dt = Math.min(100, now - this.last);
      this.last = now;
      // A resting room keeps time and draws a few frames a second.
      const draw = !this.resting || now - this.drawn >= RESTING_MS;
      if (draw) this.drawn = now;
      this.frame(dt, draw);
    };
    this.raf = requestAnimationFrame(loop);
  }

  /**
   * Whether the room is mostly covered (the chat's workspace is over it):
   * it draws a few frames a second, so a game playing on top has the
   * machine to itself.
   */
  rest(on: boolean): void {
    this.resting = on;
  }

  /**
   * Advance everything by `dt` ms and draw. The loop calls this; so does a
   * frame-by-frame recorder. A check that only follows where things move
   * passes `draw` false and skips the rendering.
   */
  frame(dt: number, draw = true): void {
    this.elapsed += dt;
    this.tweens.update(dt);
    this.rig.update(dt);
    for (const listener of this.listeners) listener(dt, this.elapsed);
    if (!draw) return;
    this.breathe();
    if (this.shadows.update(this.renderer, this.scene)) this.renderer.shadowMap.needsUpdate = true;
    this.renderer.render(this.scene, this.camera);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Lamps and bulbs pulse a little, each on its own phase. */
  private breathe(): void {
    if (!this.room || this.still.matches) return;
    const t = this.elapsed / 1000;
    this.room.glows.forEach((glow, i) => {
      const size = glow.userData.size as number;
      glow.scale.setScalar(size * (1 + 0.06 * Math.sin(t * (0.9 + (i % 5) * 0.23) + i * 1.7)));
    });
  }

  /** Project a world point to CSS pixels inside the canvas. */
  project(point: THREE.Vector3): { x: number; y: number; visible: boolean } {
    const v = point.clone().project(this.camera);
    return {
      x: (v.x * 0.5 + 0.5) * this.canvas.clientWidth,
      y: (-v.y * 0.5 + 0.5) * this.canvas.clientHeight,
      visible: v.z < 1,
    };
  }
}
